import React, { useState, useRef, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';

/**
 * ChatBottomBar — Message input with spades icon and send button.
 *
 * Adapts display based on conversation status:
 * - active: normal input
 * - request_pending: disabled, shows earn-spades prompt
 * - waiting: disabled, shows waiting text
 * - closed: disabled, shows closed text
 * - failed: disabled, shows refund text
 *
 * Includes spades balance popover on icon click.
 */
function ChatBottomBar({ status, canSend, isCreator, timeRemaining, spadesEarnable, spadesBalance, onSend }) {
  const [text, setText] = useState('');
  const [showSpades, setShowSpades] = useState(false);
  const textareaRef = useRef(null);
  const spadesRef = useRef(null);
  const spadesTimerRef = useRef(null);

  const isDisabled = !canSend;

  // Auto-resize textarea
  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    const maxHeight = 96; // ~4 lines
    el.style.height = `${Math.min(el.scrollHeight, maxHeight)}px`;
  }, [text]);

  // Spades popover auto-dismiss
  useEffect(() => {
    if (showSpades) {
      spadesTimerRef.current = setTimeout(() => setShowSpades(false), 3000);
    }
    return () => {
      if (spadesTimerRef.current) clearTimeout(spadesTimerRef.current);
    };
  }, [showSpades]);

  // Click-outside for spades popover
  useEffect(() => {
    if (!showSpades) return;
    const handler = (e) => {
      if (spadesRef.current && !spadesRef.current.contains(e.target)) {
        setShowSpades(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showSpades]);

  const handleSend = useCallback(() => {
    const trimmed = text.trim();
    if (!trimmed || isDisabled) return;
    onSend(trimmed);
    setText('');
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
    }
  }, [text, isDisabled, onSend]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const renderStatusHint = () => {
    if (status === 'new') {
      return (
        <p className="msg-bottom__hint msg-bottom__hint--muted">
          Sending your first message will place a 200 spade hold until the creator replies.
        </p>
      );
    }
    if (status === 'active' && canSend) {
      return (
        <p className="msg-bottom__hint msg-bottom__hint--teal">
          Free messaging
        </p>
      );
    }
    if (status === 'pending') {
      if (canSend && isCreator) {
        return (
          <p className="msg-bottom__hint msg-bottom__hint--teal">
            If you respond, you will receive {spadesEarnable?.toLocaleString() || '200'} spades for accepting this request.
          </p>
        );
      }
      return (
        <p className="msg-bottom__hint msg-bottom__hint--muted">
          <span className="msg-bottom__pulse-dot" />
          Waiting for a response...
        </p>
      );
    }
    if (status === 'failed') {
      if (canSend && !isCreator) {
        return (
          <p className="msg-bottom__hint msg-bottom__hint--muted">
            Creator did not respond. Your spades were returned. Send a new paid message to reopen this chat.
          </p>
        );
      }
      return <p className="msg-bottom__hint msg-bottom__hint--error">Creator failed to respond. Your spades have been returned.</p>;
    }
    if (status === 'closed') {
      if (canSend && !isCreator) {
        return (
          <p className="msg-bottom__hint msg-bottom__hint--muted">
            This chat has ended. Send a new paid message to reopen it.
          </p>
        );
      }
      return null;
    }
    if (timeRemaining) {
      const { days, hours } = timeRemaining;
      const isUrgent = days === 0 && hours <= 1;
      const isWarning = days === 0;
      const timeText = days > 0
        ? `${days} day${days !== 1 ? 's' : ''}`
        : `${hours} hour${hours !== 1 ? 's' : ''}`;
      return (
        <p className={`msg-bottom__hint ${isUrgent ? 'msg-bottom__hint--error' : isWarning ? 'msg-bottom__hint--warning' : 'msg-bottom__hint--muted'}`}>
          This chat has {timeText} remaining.
        </p>
      );
    }
    return null;
  };

  const getPlaceholder = () => {
    if (status === 'new' && canSend) return 'Type your first message...';
    if (status === 'pending' && canSend && isCreator) return 'Type your reply to accept...';
    if (status === 'closed' && canSend) return 'Type a message to reopen this chat...';
    if (status === 'failed' && canSend) return 'Type a message to reopen this chat...';
    if (status === 'closed') return 'This chat has closed.';
    if (isDisabled) return '';
    return 'Type a message...';
  };

  return (
    <div className="msg-bottom">
      {renderStatusHint()}
      <div className="msg-bottom__bar">
        <div className="msg-bottom__spades-wrapper" ref={spadesRef}>
          <button
            type="button"
            className="msg-bottom__spades-btn"
            onClick={() => setShowSpades((prev) => !prev)}
            aria-label="View spades balance"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 2C8 8 2 10 2 15a5 5 0 0 0 4.5 5c1.5 0 3-.8 3.5-2-.5 2-2 4-2 4h8s-1.5-2-2-4c.5 1.2 2 2 3.5 2A5 5 0 0 0 22 15c0-5-6-7-10-13z" />
            </svg>
          </button>
          {showSpades && (
            <div className="msg-bottom__spades-popover">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" className="msg-bottom__spades-popover-icon">
                <path d="M12 2C8 8 2 10 2 15a5 5 0 0 0 4.5 5c1.5 0 3-.8 3.5-2-.5 2-2 4-2 4h8s-1.5-2-2-4c.5 1.2 2 2 3.5 2A5 5 0 0 0 22 15c0-5-6-7-10-13z" />
              </svg>
              <span>You have {spadesBalance.toLocaleString()} spades</span>
            </div>
          )}
        </div>

        <textarea
          ref={textareaRef}
          className="msg-bottom__input"
          placeholder={getPlaceholder()}
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={handleKeyDown}
          disabled={isDisabled}
          rows={1}
        />

        <button
          type="button"
          className="msg-bottom__send-btn"
          onClick={handleSend}
          disabled={isDisabled || !text.trim()}
          aria-label="Send message"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M22 2L11 13" />
            <path d="M22 2L15 22L11 13L2 9L22 2Z" />
          </svg>
        </button>
      </div>
    </div>
  );
}

ChatBottomBar.propTypes = {
  status: PropTypes.string.isRequired,
  canSend: PropTypes.bool,
  isCreator: PropTypes.bool,
  timeRemaining: PropTypes.shape({
    days: PropTypes.number,
    hours: PropTypes.number,
  }),
  spadesEarnable: PropTypes.number,
  spadesBalance: PropTypes.number.isRequired,
  onSend: PropTypes.func.isRequired,
};

export default ChatBottomBar;
